import type { StripeError } from "@stripe/stripe-js"
import { useCallback, useEffect, useRef, useState } from "react"

import { StripeLoadError, StripeTimeoutError } from "@/utilities/expectedErrors"
import { type Flow, log } from "@/utilities/logging"
import { collectStripeDiagnostics } from "@/utilities/stripe"

const STRIPE_READY_TIMEOUT_MS = 10_000

interface UseStripePaymentElementParams {
  flow: Flow
}

interface UseStripePaymentElementReturn {
  /** Spread onto <PaymentElement {...paymentElementProps} /> */
  paymentElementProps: {
    onLoadError: (event: { elementType: "payment"; error: StripeError }) => void
    onLoaderStart: (event: { elementType: "payment" }) => void
    onReady: () => void
  }
  /** If non-null, throw this from the component render body to trigger the error boundary */
  stripeError: StripeLoadError | StripeTimeoutError | null
}

export function useStripePaymentElement({
  flow,
}: UseStripePaymentElementParams): UseStripePaymentElementReturn {
  const [stripeError, setStripeError] = useState<
    StripeLoadError | StripeTimeoutError | null
  >(null)
  const readyRef = useRef(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const onLoadError = useCallback(
    (event: { elementType: "payment"; error: StripeError }) => {
      log({
        level: "error",
        flow,
        scope: "misconfiguration",
        message: "Stripe PaymentElement failed to load",
        meta: { error: event.error, ...collectStripeDiagnostics() },
        sentry: "exception",
        exception: event.error,
      })
      setStripeError(new StripeLoadError())
    },
    [flow]
  )

  const onLoaderStart = useCallback(() => {
    log({
      level: "info",
      flow,
      scope: "info",
      message: "Stripe PaymentElement loader started",
      sentry: "breadcrumb",
    })

    // Start the "never became ready" timeout
    timeoutRef.current = setTimeout(() => {
      if (!readyRef.current) {
        const timeoutError = new Error(
          "Stripe PaymentElement did not become ready within timeout"
        )
        timeoutError.name = "StripeTimeoutError"

        log({
          level: "error",
          flow,
          scope: "misconfiguration",
          message:
            "Stripe PaymentElement never became ready (possible adblocker or network issue)",
          meta: {
            timeoutMs: STRIPE_READY_TIMEOUT_MS,
            ...collectStripeDiagnostics(),
          },
          sentry: "exception",
          exception: timeoutError,
        })
        setStripeError(new StripeTimeoutError())
      }
    }, STRIPE_READY_TIMEOUT_MS)
  }, [flow])

  const onReady = useCallback(() => {
    readyRef.current = true

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }

    log({
      level: "info",
      flow,
      scope: "info",
      message: "Stripe PaymentElement is ready",
      sentry: "breadcrumb",
    })
  }, [flow])

  return {
    paymentElementProps: {
      onLoadError,
      onLoaderStart,
      onReady,
    },
    stripeError,
  }
}
